/**
 * 맵에디터 저장본(map_data: floors[].graphJson, JointJS 셀) → export 형식
 * (parkingLotLevels[].mapData). offline-manage 가 읽는 바로 그 형식이라 같은
 * 렌더 로직을 그대로 쓸 수 있다. 변환은 에디터가 비활성 층을 복원할 때와 같은
 * 경로(fromJSON → exportGraphToJSON)를 탄다.
 */
import { dia, shapes } from '@joint/core';
import { exportGraphToJSON } from '../../map-editor-features/editor/lib/export';
import type { SiteMapLevel } from './siteMapModel';

interface SavedFloor { name?: string; order?: number; graphJson?: { cells?: unknown[] } }
interface SavedRecord { map_data: unknown }

function parseMapData(raw: unknown): Record<string, any> | null {
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw && typeof raw === 'object' ? (raw as Record<string, any>) : null;
}

/** 저장 레코드 → 층 목록. 신형(nested floors[]) 과 구형(레코드당 한 층) 둘 다 받는다. */
export function savedRecordsToFloors(records: SavedRecord[]): SavedFloor[] {
  const first = parseMapData(records[0]?.map_data);
  if (first && Array.isArray(first.floors) && first.floors.length > 0) {
    return first.floors as SavedFloor[];
  }
  return records
    .map((r) => parseMapData(r.map_data))
    .filter((m): m is Record<string, any> => !!m)
    .map((m, i) => ({ name: m.name || `B${i + 1}`, order: m.order ?? i, graphJson: m.graphJson ?? { cells: [] } }));
}

export function savedRecordsToLevels(records: SavedRecord[], lotName: string): SiteMapLevel[] {
  const floors = savedRecordsToFloors(records);
  const graphs = floors.map((f, i) => {
    const graph = new dia.Graph({}, { cellNamespace: shapes });
    if (f.graphJson && Array.isArray(f.graphJson.cells)) graph.fromJSON(f.graphJson as any);
    return { name: (f.name || `B${i + 1}`).trim(), order: typeof f.order === 'number' ? f.order : i, graph };
  });
  const exported = exportGraphToJSON(graphs, { lotName, lotCreated: new Date().toISOString() });
  return exported.data.parkingLotLevels.map((lvl) => ({
    name: lvl.name,
    mapData: lvl.mapData as unknown as Record<string, unknown>,
  }));
}
